import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Dialog, DialogBody, DialogTitle } from '../../catalyst/dialog';
import { Link } from '../../catalyst/link';
import { Skeleton } from '../../components/Skeleton';
import backend from '../../services/backend';
import { formatDateTime } from '../../lib/dateFormat';

function ErrorMessage({ message }: { message: string | null }) {
  const [expanded, setExpanded] = useState(false);
  if (!message) {
    return <div className="text-xs italic text-gray-500 dark:text-zinc-400">No error message recorded</div>;
  }
  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      title={expanded ? 'Collapse' : 'Expand'}
      className={clsx(
        'block w-full text-left font-mono text-xs whitespace-pre-wrap break-words text-gray-700 dark:text-zinc-300',
        !expanded && 'line-clamp-3',
      )}
    >
      {message.trim()}
    </button>
  );
}

export default function StageErrorsDialog({
  stageId,
  stageLabel,
  open,
  onClose,
}: {
  stageId: string;
  stageLabel: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['pipeline-stage-errors', stageId],
    queryFn: async () => (await backend.getPipelineStageErrors(stageId)).data,
    enabled: open,
  });
  return (
    <Dialog open={open} onClose={onClose} size="3xl">
      <DialogTitle>{stageLabel} — errors (last 60 min)</DialogTitle>
      <DialogBody>
        {isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
        {isError && (
          <div className="text-sm text-red-600 dark:text-red-400">
            Failed to load errors — close the dialog and try again.
          </div>
        )}
        {!isLoading && data && data.errors.length === 0 && (
          <div className="text-sm text-gray-500 dark:text-zinc-400">
            No errors in the last 60 minutes — they may have aged out of the window.
          </div>
        )}
        {!isLoading && data && data.errors.length > 0 && (
          <ul className="flex flex-col divide-y divide-gray-200 dark:divide-zinc-700">
            {data.errors.map((error) => (
              <li key={error.id} className="flex flex-col gap-1 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <Link
                    href={data.kind === 'page' ? `/document-pages/${error.id}` : `/documents/${error.id}`}
                    className="truncate text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {error.fileName ?? error.id}
                  </Link>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-zinc-400">
                    {error.occurredAt ? formatDateTime(error.occurredAt) : '—'}
                  </span>
                </div>
                <ErrorMessage message={error.errorMessage} />
              </li>
            ))}
          </ul>
        )}
      </DialogBody>
    </Dialog>
  );
}
